import {
  Avatar,
  AvatarGroup,
  Badge,
  BadgeProps,
  CountryCode,
  FlagComponent,
} from '@commenda/kakashi';
import { Country } from 'country-state-city';
import type { ActiveService } from './useActiveServices';

// Shared 4-column grid template so the header row (in ActiveServices) and the
// data rows stay column-aligned. Single source of truth — don't inline it
// separately in either place, or the columns drift.
export const ACTIVE_SERVICES_GRID_CLASSNAME =
  'grid grid-cols-[minmax(0,2fr)_minmax(0,1.5fr)_130px_minmax(0,1.3fr)] items-center gap-lg px-lg';

const STATUS_DISPLAY: Record<
  string,
  { label: string; variant: BadgeProps['variant'] }
> = {
  NotStarted: { label: 'Not started', variant: 'gray' },
  InProgress: { label: 'In progress', variant: 'blue' },
  Completed: { label: 'Completed', variant: 'success' },
};

interface ActiveServicesRowProps {
  service: ActiveService;
  onOpen: (service: ActiveService) => void;
}

export const ActiveServicesRow = ({
  service,
  onOpen,
}: ActiveServicesRowProps) => {
  const { title, entity, country, status, pendingOn } = service;

  const badge = STATUS_DISPLAY[status] ?? { label: status, variant: 'gray' };
  const isIncorporation = title?.toLowerCase().includes('incorporation');
  const [assignee] = pendingOn;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(service)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') onOpen(service);
      }}
      className={`${ACTIVE_SERVICES_GRID_CLASSNAME} cursor-pointer border-b border-border-secondary py-md text-left transition-colors hover:bg-bg-secondary`}
    >
      <span className="truncate txt-small-regular text-text-primary">
        {title}
      </span>

      <div className="flex items-center gap-md overflow-hidden">
        {country && (
          <FlagComponent
            _className="size-5 shrink-0"
            country={country as CountryCode}
            countryName={Country.getCountryByCode(country)?.name}
          />
        )}
        {entity?.name ? (
          <span className="truncate txt-small-regular text-text-primary">
            {entity.name}
          </span>
        ) : isIncorporation ? (
          <span className="truncate txt-small-regular italic text-text-disabled">
            New {country} entity
          </span>
        ) : (
          <span className="text-text-disabled">—</span>
        )}
      </div>

      <div>
        <Badge variant={badge.variant} size="sm">
          {badge.label}
        </Badge>
      </div>

      <div className="overflow-hidden">
        {pendingOn.length === 0 && (
          <span className="txt-small-regular text-text-disabled">
            Not yet assigned
          </span>
        )}
        {pendingOn.length === 1 && (
          <div className="flex items-center gap-md">
            <Avatar
              size="xs"
              name={assignee?.name ?? 'Commenda'}
              colorSeed={assignee?.email ?? assignee?.name ?? 'Commenda'}
            />
            <span className="truncate txt-small-regular text-text-primary">
              {assignee?.name ?? 'Commenda'}
            </span>
          </div>
        )}
        {pendingOn.length > 1 && (
          <AvatarGroup
            size="xs"
            avatars={pendingOn.map(({ name, email }) => ({
              name: name ?? '',
              colorSeed: email ?? name ?? '',
              tooltip: { content: email ? `${name} (${email})` : (name ?? '') },
            }))}
          />
        )}
      </div>
    </div>
  );
};
